import { WarningTriangle } from 'iconoir-react'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import { Button } from 'app/components/ui/button'
import { FilterBadge } from '../shared/styled/FilterBadge'
import { Tooltip } from 'app/components/Tooltip'
import Companies from 'app/components/Icon/CompaniesIcon'
import { resolveLocationScope } from 'app/lib/locationScope'
import { OptionType } from 'types/OptionType'

export default function LocationSelector({
  selectedOptions,
  locationOptions,
  deselectAllOptions,
  onChange,
  hideLabel,
}: {
  selectedOptions?: OptionType[]
  locationOptions: OptionType[]
  deselectAllOptions: OptionType[]
  onChange: (options: OptionType[]) => void
  /** Hide the "Locations" text (icon + count only) to save width on mobile. */
  hideLabel?: boolean
}) {
  // Both badges answer one question about one set: of the locations in view, how many are
  // reporting and how many are not. So they always sum to the size of that set, and the amber
  // count is the same number the coverage notice puts behind "All N". Previously the blue badge
  // answered a different question (how many have a PMS connection at all) and silently changed
  // meaning to "how many you selected" the moment you picked something, which is why the two
  // could never be reconciled by looking at them.
  //
  // "Reporting" means a fresh PMS sync, which is a time-relative judgement the API cannot make,
  // so both counts come from the fetched options rather than a server aggregate. That is exact
  // up to the ~1000-row page cap on the locations query.
  const scope = resolveLocationScope(locationOptions, selectedOptions)
  const reportingCount = scope.filter((option) => option.meta?.integrationStatus === `integrated`).length
  const notReportingCount = scope.length - reportingCount

  return (
    <ComboMultiSelect
      name="location-selector"
      customButton={
        <Button
          variant="tertiary"
          size="sm"
          className={
            (selectedOptions && selectedOptions?.length > 0) || locationOptions.length > 0
              ? `text-blue-600 hover:text-blue-600`
              : undefined
          }
        >
          <Companies width={16} height={16} />
          {!hideLabel && <span className="text-ui-xs">Locations</span>}
          <Tooltip
            placement="bottom"
            message={`${reportingCount} of ${scope.length} location${scope.length === 1 ? `` : `s`} reporting complete data`}
          >
            <span className="flex">
              <FilterBadge count={reportingCount} />
            </span>
          </Tooltip>
          <NotReportingBadge count={notReportingCount} />
        </Button>
      }
      inputClassName="flex !h-8 w-full cursor-pointer items-center justify-between rounded border border-default bg-surface !px-2 !text-body-sm focus:border-subtle focus:outline-none focus:ring-1 focus:ring-strong"
      optionsContainerClassName="max-h-[70vh] !relative min-w-[280px] w-max"
      values={selectedOptions}
      onChange={onChange}
      options={locationOptions}
      deselectAllOptions={deselectAllOptions}
      enableDeselectAllOption
    />
  )
}

function NotReportingBadge({ count }: { count: number }) {
  if (count === 0) return null

  // The warning glyph is the whole point of this badge. It is the same icon, at the same amber,
  // that heads the coverage notice under the filter row, so the two read as one statement
  // instead of an unexplained second number sitting beside the first.
  return (
    <Tooltip
      placement="bottom"
      message={`${count} location${count === 1 ? `` : `s`} not sending complete data. See the note below the filters.`}
    >
      <span className="flex h-5 items-center gap-1 rounded border-[0.5px] border-amber-300 bg-amber-100 px-1.5 text-label-xs text-amber-800">
        <WarningTriangle className="h-3 w-3 shrink-0" strokeWidth={2.5} />
        {count}
      </span>
    </Tooltip>
  )
}
